import React, { useState } from "react";

/**
 * CRM-R05 · Tablero Kanban de estados comerciales con arrastrar y soltar.
 * Al soltar una tarjeta en otra columna llama a `onMove(item, targetEstado)`,
 * que en el padre es el MISMO `changeEstado` del formulario (mismas
 * validaciones, mismos modales de motivo/confirmación y validación del backend).
 *
 * Props:
 *   items: [{ id, estado_licitacion, cliente, title, objeto, presupuesto, ... }]
 *   onMove(item, targetEstado)
 *   canMove(fromEstado, toEstado) -> bool
 */
const COLUMNS = [
  { key: "PUBLICADA", label: "Publicadas", color: "border-blue-300 bg-blue-50/40", head: "text-blue-800" },
  { key: "POR_DECIDIR", label: "Por decidir", color: "border-amber-300 bg-amber-50/40", head: "text-amber-800" },
  { key: "ESTUDIO", label: "En estudio", color: "border-violet-300 bg-violet-50/40", head: "text-violet-800" },
  { key: "PRESENTADA", label: "Presentadas", color: "border-emerald-300 bg-emerald-50/40", head: "text-emerald-800" },
  { key: "NO_PRESENTADA", label: "No presentadas", color: "border-rose-300 bg-rose-50/40", head: "text-rose-800" },
];
const ESTUDIO_ALIASES = new Set(["ESTUDIO", "EN_ESTUDIO"]);
const fmtEUR = (n) => (!n ? "—" : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", notation: "compact", maximumFractionDigits: 1 }).format(n));

const colOf = (estado) => (ESTUDIO_ALIASES.has(estado) ? "ESTUDIO" : estado);

export const MercadoKanban = ({ items = [], onMove, canMove }) => {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const byCol = {};
  COLUMNS.forEach((c) => (byCol[c.key] = []));
  items.forEach((it) => {
    const c = colOf(it.estado_licitacion);
    if (byCol[c]) byCol[c].push(it);
  });

  const dragItem = items.find((i) => i.id === dragId);
  const allowedHere = (colKey) => {
    if (!dragItem) return false;
    const from = colOf(dragItem.estado_licitacion);
    if (from === colKey) return false;
    return canMove ? canMove(dragItem.estado_licitacion, colKey) : true;
  };

  const handleDrop = (colKey) => {
    setOverCol(null);
    const it = items.find((i) => i.id === dragId);
    setDragId(null);
    if (!it) return;
    if (colOf(it.estado_licitacion) === colKey) return;
    onMove?.(it, colKey);
  };

  return (
    <div data-testid="mercado-kanban">
      <p className="text-[11px] text-gray-500 mb-2 flex items-center gap-1.5" data-testid="kanban-scope-note">
        Mostrando <b>{items.length}</b> oportunidad{items.length === 1 ? "" : "es"} del pipeline activo según los filtros aplicados. Arrastra una tarjeta a otra columna para cambiar su estado (mismas validaciones del formulario).
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
      {COLUMNS.map((col) => {
        const droppable = allowedHere(col.key);
        const isOver = overCol === col.key;
        return (
          <div
            key={col.key}
            data-testid={`kanban-col-${col.key}`}
            onDragOver={(e) => { if (droppable) { e.preventDefault(); setOverCol(col.key); } }}
            onDragLeave={() => isOver && setOverCol(null)}
            onDrop={() => droppable && handleDrop(col.key)}
            className={`rounded-xl border p-2 min-h-[200px] transition ${col.color} ${isOver && droppable ? "ring-2 ring-indigo-400" : ""} ${dragItem && !droppable && colOf(dragItem.estado_licitacion) !== col.key ? "opacity-50" : ""}`}>
            <div className={`flex items-center justify-between mb-2 px-1 ${col.head}`}>
              <span className="text-[11px] font-bold uppercase tracking-wide">{col.label}</span>
              <span className="text-[10px] font-mono bg-white/70 px-1.5 rounded-full">{byCol[col.key].length}</span>
            </div>
            <div className="space-y-1.5">
              {byCol[col.key].map((it) => (
                <div
                  key={it.id}
                  draggable
                  onDragStart={() => setDragId(it.id)}
                  onDragEnd={() => { setDragId(null); setOverCol(null); }}
                  data-testid={`kanban-card-${it.id}`}
                  className="bg-white rounded-lg border border-gray-200 p-2 shadow-sm cursor-grab active:cursor-grabbing hover:border-indigo-300 hover:shadow">
                  <p className="text-[11px] font-semibold text-gray-800 leading-tight line-clamp-2" title={it.cliente || it.title}>
                    {it.cliente || it.title || it.objeto || "Oportunidad"}
                  </p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[9px] text-gray-400 font-mono truncate max-w-[70%]" title={it.expediente}>{it.expediente || ""}</span>
                    <span className="text-[10px] font-mono text-emerald-700">{fmtEUR(it.presupuesto)}</span>
                  </div>
                </div>
              ))}
              {byCol[col.key].length === 0 && (
                <p className="text-[10px] text-gray-300 text-center py-4">Sin oportunidades</p>
              )}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
};

export default MercadoKanban;
